import React, { useEffect, useState, useRef } from "react";
import BodyHeader from "../../components/customComp/BodyHeader";
import PatientsBody from "./patients/PatientsBody";
import FiltersSideBar from "../../components/sideBar/FiltersSideBar";
import constants from "../../utils/constants";
import utils from "../../utils/utils";
import SVGraphics from "../../assets/SVGraphics";
import { authenticationService } from "../../services/AuthinticationService";
import ImportButton from "../../components/customComp/ImportButton";
import handleDicomUpload, { setLogging } from "../../utils/uploadDicomHelper";
import SystemMessage from "../../components/customComp/SystemMessage";

// logging (tweak as needed)
setLogging({
  UPLOAD_START: true,
  FILE_FILTER: true,
  METADATA: true,
  ZIP: true,
  S3: true,
  S3_PROGRESS: true,
  PRESIGN: true,
  COMMIT: true,
  SUCCESS: true,
  ERROR: true,
  CLEANUP: true,
  VALIDATION: false,
  RETRY: true,
});

const PatientListComp = (props) => {
  // UI state
  const [isSideBarOpen, setIsSideBarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ 
    show: false, 
    percentage: 0, 
    stage: 'Preparing...' 
  });
  const [patientsData, setPatientsData] = useState([]);
  const [filtersObj, setFiltersObj] = useState({});
  const [searchInput, setSearchInput] = useState("");
  const fileInputRef = useRef(null);

  // System message (error/warning/success)
  const [sysMsg, setSysMsg] = useState(null);
  const [sysMsgKey, setSysMsgKey] = useState(0);
  const showMsg = (m) => {
    setSysMsgKey((k) => k + 1);
    setSysMsg(m);
  };

  const openSideBar = () => {
    document.body.style.overflow = "hidden";
    setIsSideBarOpen(true);
  };
  const closeSideBar = () => {
    document.body.style.overflow = "auto";
    setIsSideBarOpen(false);
  };

  const filtersCategory = [
    {
      label: "Filters",
      inputs: [
        {
          name: "status",
          label: "All Statuses",
          data: constants.statusFilterOptions,
          type: "dropdown",
        },
        {
          name: "finding",
          label: "All Findings",
          data: constants.findingsFilterOptions,
          type: "dropdown",
        },
        {
          name: "onlyFindings",
          label: "Show Findings Only",
          data: constants.findingsFilterOptions,
          type: "switch",
        },
      ],
    },
  ];

  // data fetching
  useEffect(() => {
    getAllPatients(filtersObj).then(setData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersObj]);

  const getAllPatients = async (filters) =>
    props.restApi.getAllPatients(filters);

  const setData = (p) => {
    if (!Array.isArray(p)) return;
    const sorted = p.map((patient) => {
      if (Array.isArray(patient.exams)) {
        patient.exams = [...patient.exams].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      }
      return patient;
    });
    setPatientsData(sorted);
  };

  const handleApplyFilters = (filters) => {
    props.restApi.getAllPatients(filters).then((p) => {
      setPatientsData(p);
      closeSideBar();
    });
  };

  const onAdvanceFilterChange = (name, value) => {
    const next = { ...filtersObj, [name]: value };
    const isAll =
      (name === "name" || name === "finding") &&
      typeof value?.indexOf === "function" &&
      value.indexOf("All") >= 0;

    if (isAll || (Array.isArray(value) && value.length === 0)) {
      delete next[name];
    }
    setFiltersObj(next);
  };

  const clearFilters = () => {
    setFiltersObj({});
    handleApplyFilters({});
  };

  // debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      const f = { ...filtersObj };
      if (searchInput.trim()) f.search = searchInput.trim();
      getAllPatients(f).then(setData);
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, filtersObj]);

  // file picker
  const ImportDICOM = () => fileInputRef.current?.click();

  const handleUploadError = (err) => {
    // Prefer the formatted error shape from handleDicomUpload
    debugger;
    const detailsBody =
      err?.details?.body ||
      err?.response?.data ||
      err?.originalError?.response?.data ||
      err?.data ||
      err?.body;

    const msg =
      err?.message || // formattedError.message
      (typeof detailsBody === "string" && detailsBody) ||
      detailsBody?.error ||
      detailsBody?.message ||
      null;

    if (!msg) {
      console.error("Non-backend error (suppressed in UI):", err);
      // return;
    }

    debugger

    const status =
      err?.details?.status ??
      err?.status ??
      err?.response?.status ??
      err?.originalError?.response?.status ??
      detailsBody?.code ??
      undefined;

    showMsg({
      type: "error",
      title: `Upload Error${status ? ` (${status})` : "Somthing went wrong"}`,
      message: msg ? String(msg) : "Something went wrong",
    });
  };

  // Progress callback for upload
  const handleUploadProgress = (percentage, stage) => {
    setUploadProgress({
      show: true,
      percentage,
      stage
    });
  };

  // main upload
  const handleFolderSelect = async (event) => {
    setUploadProgress({ show: true, percentage: 0, stage: 'Starting upload...' });
    
    await handleDicomUpload(
      event,
      setIsLoading,
      setData,
      filtersObj,
      getAllPatients,
      handleUploadError,
      handleUploadProgress
    ).then((res) => {
      // Hide progress bar
      setTimeout(() => setUploadProgress({ show: false, percentage: 0, stage: '' }), 1000);
      
      if (res?.success) {
        showMsg({
          type: "success",
          title: "Upload received",
          message:
            "Your study was uploaded and submitted for analysis. It will appear here after processing.",
        });
      }
    }).catch(() => {
      // Hide progress bar on error
      setUploadProgress({ show: false, percentage: 0, stage: '' });
    });
  };

  return (
    <div className="w-full px-12 pt-4 pb-16 h-full max-h-full overflow-hidden box-border bg-[#111]">
      <div className="flex justify-between w-100">
        <BodyHeader
          title={"Patient List"}
          includeButton={false}
          includeSubtext={true}
          subText={
            authenticationService.currentUserValue.profile.HCP
              ? `(${authenticationService.currentUserValue.profile.HCP})`
              : ""
          }
        />
        <div className="flex items-center gap-3">
          <div className="patientSearch z-2000 bg-[#2B2B2B] hover:border-blue-500 focus:border-blue-500 active:border-blue-700 w-[394px] relative h-[40px] flex text-[#C1C1C1] items-center px-[17px] rounded-[8px]">
            <div className="pr-[5px]">
              <SVGraphics svgname="searchGlass" width="12px" height="12px" />
            </div>
            <input
              placeholder="Search"
              className="bg-transparent font-[14px]"
              onChange={(e) => setSearchInput(e.target.value)}
              value={searchInput}
            />
            {patientsData.length > 0 && searchInput.length > 0 && (
              <div className="patientSearchResults absolute z-[1000] py-[10px] left-0 top-[100%] text-white w-full h-30 flex flex-col bg-[#2B2B2B] rounded-[8px]">
                {patientsData.map((patient, i) => (
                  <div
                    onClick={() => props.addTab("patient", patient.patientID)}
                    className="patientResult cursor-pointer hover:bg-[#6c6c6c] flex px-[23px] py-[9px] gap-[23px] items-center"
                    key={`${patient.name}${i}`}>
                    <div className="rounded-full bg-blue-700 w-[30px] h-[30px] flex items-center justify-center">
                      <SVGraphics
                        svgname="PatientIcon"
                        width="19"
                        height="18px"
                      />
                    </div>
                    <div className="flex flex-col">
                      <div className="resultTitle">{patient.name}</div>
                      <div className="resultText text-[12px] text-[#C1C1C1]">
                        {utils.insertDash(patient.patientID)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <ImportButton onClick={ImportDICOM} text="Import DICOM" />
        </div>
      </div>

      <div className="h-full max-h-full overflow-clip">
        <div className="flex h-fit w-100 justify-between mt-8">
          <FiltersSideBar
            restApi={props.restApi}
            filtersObj={filtersObj}
            onFilterChange={(name, value) => onAdvanceFilterChange(name, value)}
            onApplyClick={() => handleApplyFilters(filtersObj)}
            sideBarWidth={"w-fit"}
            gridStyle={""}
            filtersCategory={filtersCategory}
            setIsLoading={() => setIsLoading(!isLoading)}
            closeSideBar={() => closeSideBar()}
            clearFilters={() => clearFilters()}
            isSideBarOpen={isSideBarOpen}
            hasClear={false}
          />
        </div>
        <PatientsBody patientsData={patientsData} addTab={props.addTab} />
      </div>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFolderSelect}
        webkitdirectory="true"
        mozdirectory="true"
        accept=".dcm"
      />

      {sysMsg && (
        <SystemMessage
          key={sysMsgKey}
          type={sysMsg.type}
          title={sysMsg.type}
          message={String(sysMsg?.message || "")}
          handleClose={() => setSysMsg(null)}
        />
      )}

      {/* Upload Progress Bar */}
      {uploadProgress.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000]">
          <div className="bg-[#2B2B2B] p-6 rounded-lg border border-gray-600 max-w-md w-full mx-4">
            <div className="text-white text-lg mb-4 text-center font-medium">
              Uploading DICOM Study
            </div>
            <div className="text-[#C1C1C1] text-sm mb-3 text-center">
              {uploadProgress.stage}
            </div>
            <div className="w-full bg-gray-700 rounded-full h-3 mb-3">
              <div 
                className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${uploadProgress.percentage}%` }}
              />
            </div>
            <div className="text-[#C1C1C1] text-sm text-center">
              {uploadProgress.percentage}%
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientListComp;
